import { useQuery } from '@tanstack/react-query'
import { percentualPpm, ROTULO_CENARIO, type Cenario, type ResultadoCenario, type ResultadoHibrido, type SimulacaoResultado } from '@via-tax/contracts'
import { Aviso, Cartao, EstadoVazio, Selo } from '@via-tax/ui'
import { useExecutarSimulacao } from '../components/ExecutarSimulacao'
import type { Tela } from '../components/Shell'
import { dataBr, reais, reaisCompacto } from '../formatos'
import type { ContextoSessao } from '../tipos'
import { useTRPC } from '../trpc'

/**
 * Panorama 2027 → 2033 (arquitetura §10.3, F7): a trajetória da carga sobre consumo na transição —
 * CBS cheia a partir de 2027, ICMS/ISS caindo 10% ao ano de 2029 a 2032 enquanto o IBS sobe, e o
 * sistema novo inteiro em 2033. Faixa dos três cenários sempre visível: nenhuma dessas alíquotas existe ainda.
 */

const pct = percentualPpm
const sinal = (c: number) => (c > 0 ? `+${reais(c)}` : reais(c))
const CENARIOS_FAIXA: Cenario[] = ['OTIMISTA', 'BASE', 'CONSERVADOR']

export function Panorama({ contexto, ir }: { contexto: ContextoSessao; ir: (t: Tela) => void }) {
  const trpc = useTRPC()
  const podeExecutar = (contexto.capacidades as string[]).includes('executar_simulacao')
  const ultima = useQuery({
    ...trpc.simulacao.ultima.queryOptions(),
    refetchInterval: (q) => (q.state.data?.recente?.status === 'PROCESSANDO' ? 3000 : false),
  })
  const recente = ultima.data?.recente
  const concluida = ultima.data?.concluida
  const processando = recente?.status === 'PROCESSANDO'
  const simples = contexto.empresaAtiva?.regimeTributario === 'simples_nacional'
  const execucao = useExecutarSimulacao({ premissasAtuais: concluida?.premissas ?? recente?.premissas, processando, jaRodou: !!concluida, simples, operacoes: concluida?.resultado.operacoes })
  const anos = concluida ? [...new Set(concluida.resultado.resultados.map((x) => x.ano))].sort() : []
  const temTrajetoria = anos.some((a) => a >= 2029)

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Panorama 2027 → 2033</h1>
          <p className="mt-1 max-w-2xl text-sm text-tinta-2">
            A mesma operação dos últimos 12 meses atravessando a transição inteira: CBS a partir de 2027, ICMS e ISS caindo de 2029 a 2032 enquanto o IBS sobe, e o sistema novo completo
            em 2033. Para planejar — a decisão de agora está no Foco 2027.
          </p>
        </div>
        {podeExecutar && execucao.botoes}
      </header>
      {podeExecutar && execucao.painel}
      {processando && <Aviso tom="info">Calculando os sete anos nos três cenários — a tela atualiza sozinha.</Aviso>}
      {recente?.status === 'ERRO' && <Aviso>A última simulação não terminou: {recente.erro}</Aviso>}

      {ultima.isPending ? (
        <p className="text-sm text-tinta-3">Carregando…</p>
      ) : !concluida || !temTrajetoria ? (
        !processando && (
          <EstadoVazio titulo={concluida ? 'A última simulação só tem 2027 e 2028' : 'Nenhuma simulação ainda'}>
            {podeExecutar ? 'Rode a simulação: ela passa a calcular de 2027 a 2033.' : 'O escritório ainda não rodou a trajetória desta empresa.'}
          </EstadoVazio>
        )
      ) : (
        <Trajetoria r={concluida.resultado} meta={{ criadoPor: concluida.criadoPor, criadoEm: concluida.criadoEm }} ir={ir} />
      )}
    </div>
  )
}

function Trajetoria({ r, meta, ir }: { r: SimulacaoResultado; meta: { criadoPor: string; criadoEm: string | Date }; ir: (t: Tela) => void }) {
  const anos = [...new Set(r.resultados.map((x) => x.ano))].sort()
  const de = (ano: number, c: Cenario) => r.resultados.find((x) => x.ano === ano && x.cenario === c)!
  const base = anos.map((a) => de(a, 'BASE'))
  const simples = Boolean(base[0]!.simples)
  const hoje = base[0]!.atual.total
  const receita = base[0]!.receita
  const is = Math.max(...base.map((c) => c.impostoSeletivo?.receitaSujeita ?? 0))
  const pleno = base.find((c) => c.aliquotas.ibsPleno)?.aliquotas.ibsPleno ?? null
  const final = base[base.length - 1]!

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-3 md:grid-cols-3">
        <Numero rotulo="Carga hoje (regras de 2026)" valor={reais(hoje)} nota={`${pct(Math.round((hoje * 1e6) / Math.max(1, receita)), 1)} da receita de ${reaisCompacto(receita)}`} />
        <Numero rotulo={`Carga em ${final.ano} (cenário base)`} valor={reais(final.nova.total)} nota={`faixa ${reaisCompacto(de(final.ano, 'OTIMISTA').nova.total)} a ${reaisCompacto(de(final.ano, 'CONSERVADOR').nova.total)}`} />
        <Numero rotulo={`Diferença em ${final.ano}`} valor={sinal(final.variacao)} nota={`${final.variacaoSobreReceita > 0 ? '+' : ''}${pct(final.variacaoSobreReceita)} p.p. sobre a receita`} tom={final.variacao > 0 ? 'alerta' : 'ok'} />
      </div>

      <Cartao titulo="Carga sobre consumo por ano">
        <Grafico anos={anos} hoje={hoje} de={de} />
      </Cartao>

      <Cartao titulo={simples ? 'Ano a ano no Simples Nacional' : 'Ano a ano (cenário base)'}>
        {simples ? <TabelaSimples base={base} hibrido={r.hibrido ?? []} ir={ir} /> : <TabelaRegular base={base} />}
      </Cartao>

      <div className="grid gap-5 lg:grid-cols-2">
        <Cartao titulo="De onde vêm as alíquotas">
          <ul className="flex flex-col gap-2 text-sm text-tinta-2">
            <li>
              <strong className="text-tinta">CBS</strong>: {pct(base[0]!.aliquotas.cbsReferencia)} de referência no cenário base (2027–2028 com −0,1 p.p., art. 347). De 2029 em diante o
              Senado recalcula a cada ano; aqui repete o cenário de 2027.
            </li>
            {pleno && (
              <li>
                <strong className="text-tinta">IBS pleno</strong>: {pct(pleno.aliquota)} no cenário base —{' '}
                {pleno.url ? (
                  <a className="underline" href={pleno.url} target="_blank" rel="noreferrer">
                    {pleno.fonte}
                  </a>
                ) : (
                  pleno.fonte
                )}
                . De 2029 a 2032 vale 10%, 20%, 30% e 40% dele (art. 367, V).
              </li>
            )}
            <li>
              <strong className="text-tinta">ICMS e ISS</strong>: os de hoje, reduzidos em 10%, 20%, 30% e 40% de 2029 a 2032 e extintos em 2033 (arts. 501 e 508).
            </li>
            <li className="text-xs text-tinta-3">
              Cenários: {CENARIOS_FAIXA.map((c) => `${ROTULO_CENARIO[c]} CBS ${pct(de(final.ano, c).aliquotas.cbs)} + IBS ${pct(de(final.ano, c).aliquotas.ibs)}`).join(' · ')} em{' '}
              {final.ano}. <Selo tom="alerta">estimativas</Selo>
            </li>
          </ul>
        </Cartao>
        <Cartao titulo="O que não está no número">
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-tinta-2">
            {is > 0 ? (
              <li>
                <strong className="text-alerta">Imposto Seletivo</strong>: {reais(is)} por ano de vendas de produtos sujeitos ao IS. As alíquotas ainda não existem em lei, então o valor
                não entra na carga.
              </li>
            ) : (
              <li>Nenhuma venda classificada como sujeita ao Imposto Seletivo.</li>
            )}
            <li>Preços e volumes constantes, sem crescimento da receita.</li>
            <li>Redução da CBS a partir de 2030 se a arrecadação passar do teto (art. 368).</li>
            {r.qualidade.meses < 12 && <li>Ano-base com {r.qualidade.meses} mês(es) de notas, projetado para 12.</li>}
          </ul>
        </Cartao>
      </div>

      <p className="text-xs text-tinta-3">
        Motor {r.proveniencia.versaoEngine} · parâmetros {r.proveniencia.versaoParametros} · base {r.proveniencia.versaoKb} · calculado em {dataBr(r.proveniencia.calculadoEm)} · pedido por{' '}
        {meta.criadoPor} em {dataBr(meta.criadoEm)}
      </p>
    </div>
  )
}

function Numero({ rotulo, valor, nota, tom }: { rotulo: string; valor: string; nota: string; tom?: 'ok' | 'alerta' }) {
  return (
    <div className="rounded-lg border border-linha bg-painel px-4 py-3">
      <p className="text-xs text-tinta-3">{rotulo}</p>
      <p className={`num mt-1 text-2xl font-semibold ${tom === 'alerta' ? 'text-alerta' : tom === 'ok' ? 'text-ok' : 'text-tinta'}`}>{valor}</p>
      <p className="mt-0.5 text-xs text-tinta-2">{nota}</p>
    </div>
  )
}

/** Barras por ano (cenário base) com a faixa otimista–conservador por trás; "hoje" como referência. */
function Grafico({ anos, hoje, de }: { anos: number[]; hoje: number; de: (ano: number, c: Cenario) => ResultadoCenario }) {
  const pontos = anos.map((a) => ({ ano: a, base: de(a, 'BASE').nova.total, min: Math.min(...CENARIOS_FAIXA.map((c) => de(a, c).nova.total)), max: Math.max(...CENARIOS_FAIXA.map((c) => de(a, c).nova.total)) }))
  const topo = Math.max(1, hoje, ...pontos.map((p) => p.max))
  const h = (v: number) => `${Math.max(0, (v / topo) * 100)}%`
  return (
    <div className="flex flex-col gap-3">
      <div className="relative flex h-56 items-end gap-3 overflow-x-auto border-b border-linha pb-0">
        <div className="pointer-events-none absolute inset-x-0 border-t border-dashed border-tinta-3" style={{ bottom: h(hoje) }}>
          <span className="absolute -top-4 right-0 text-[10px] text-tinta-3">hoje {reaisCompacto(hoje)}</span>
        </div>
        {pontos.map((p) => (
          <div key={p.ano} className="relative flex h-full min-w-12 flex-1 flex-col items-center justify-end" title={`${p.ano}: base ${reais(p.base)} · faixa ${reais(p.min)} a ${reais(p.max)}`}>
            <span className="num mb-1 text-[10px] text-tinta-2">{reaisCompacto(p.base)}</span>
            <div className="relative w-full max-w-10" style={{ height: h(p.max) }}>
              <div className="absolute inset-x-0 bottom-0 rounded-t bg-navy/15" style={{ height: '100%' }} />
              <div className="absolute inset-x-1 bottom-0 rounded-t bg-navy" style={{ height: `${(p.base / Math.max(1, p.max)) * 100}%` }} />
              <div className="absolute inset-x-0 border-t-2 border-ok/70" style={{ bottom: `${(p.min / Math.max(1, p.max)) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-3">
        {pontos.map((p) => (
          <span key={p.ano} className="num min-w-12 flex-1 text-center text-[11px] text-tinta-2">
            {p.ano}
          </span>
        ))}
      </div>
      <p className="flex flex-wrap gap-4 text-[11px] text-tinta-3">
        <span>
          <span className="inline-block h-2 w-3 rounded-sm bg-navy" /> cenário base
        </span>
        <span>
          <span className="inline-block h-2 w-3 rounded-sm bg-navy/15" /> até o conservador
        </span>
        <span>
          <span className="inline-block h-0.5 w-3 bg-ok/70 align-middle" /> otimista
        </span>
        <span>linha tracejada: carga de hoje</span>
      </p>
    </div>
  )
}

function TabelaRegular({ base }: { base: ResultadoCenario[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="text-left text-xs text-tinta-3">
            <th className="pb-2 font-medium">Ano</th>
            <th className="pb-2 text-right font-medium">CBS</th>
            <th className="pb-2 text-right font-medium">IBS</th>
            <th className="pb-2 text-right font-medium">ICMS/ISS restante</th>
            <th className="pb-2 text-right font-medium">CBS + IBS devidos</th>
            <th className="pb-2 text-right font-medium">(−) créditos</th>
            <th className="pb-2 text-right font-medium">ICMS + ISS</th>
            <th className="pb-2 text-right font-medium">Carga</th>
            <th className="pb-2 text-right font-medium">vs. hoje</th>
          </tr>
        </thead>
        <tbody>
          {base.map((c) => (
            <tr key={c.ano} className="border-t border-linha">
              <td className="py-1.5 font-medium">{c.ano}</td>
              <td className="num py-1.5 text-right">{pct(c.aliquotas.cbs)}</td>
              <td className="num py-1.5 text-right">{pct(c.aliquotas.ibs)}</td>
              <td className="num py-1.5 text-right">{pct(c.aliquotas.icmsIssFator ?? 1_000_000, 0)}</td>
              <td className="num py-1.5 text-right">{reais(c.nova.cbs + c.nova.ibs)}</td>
              <td className="num py-1.5 text-right">{reais(-c.nova.creditos)}</td>
              <td className="num py-1.5 text-right">{reais(c.nova.icms + c.nova.iss)}</td>
              <td className="num py-1.5 text-right font-semibold">{reais(c.nova.total)}</td>
              <td className={`num py-1.5 text-right ${c.variacao > 0 ? 'text-alerta' : 'text-ok'}`}>{sinal(c.variacao)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TabelaSimples({ base, hibrido, ir }: { base: ResultadoCenario[]; hibrido: ResultadoHibrido[]; ir: (t: Tela) => void }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="text-left text-xs text-tinta-3">
              <th className="pb-2 font-medium">Ano</th>
              <th className="pb-2 text-right font-medium">Alíquota efetiva</th>
              <th className="pb-2 text-right font-medium">Puro: DAS</th>
              <th className="pb-2 text-right font-medium">Híbrido: DAS + CBS + IBS</th>
              <th className="pb-2 text-right font-medium">Faixa híbrido × puro</th>
              <th className="pb-2 text-right font-medium">Híbrido × puro (base)</th>
            </tr>
          </thead>
          <tbody>
            {base.map((c) => {
              const h = hibrido.find((x) => x.ano === c.ano)
              return (
                <tr key={c.ano} className="border-t border-linha">
                  <td className="py-1.5 font-medium">{c.ano}</td>
                  <td className="num py-1.5 text-right">{c.simples ? pct(c.simples.efetivaNova) : '—'}</td>
                  <td className="num py-1.5 text-right">{reais(c.nova.das)}</td>
                  <td className="num py-1.5 text-right">
                    {h?.aPagar ? (
                      <>
                        {reais(h.aPagar.hibrido.total)}
                        <span className="block text-[11px] text-tinta-3">
                          DAS {reaisCompacto(h.aPagar.hibrido.das)} + CBS/IBS {reaisCompacto(h.aPagar.hibrido.cbs + h.aPagar.hibrido.ibs)}
                        </span>
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="num py-1.5 text-right text-xs text-tinta-2">{h?.faixa ? `${reaisCompacto(h.faixa.otimista.diferenca)} a ${reaisCompacto(h.faixa.conservador.diferenca)}` : '—'}</td>
                  <td className="py-1.5 text-right">
                    {h ? (
                      <span className="flex items-center justify-end gap-2">
                        <span className="num text-xs text-tinta-2">{sinal(h.diferenca)}</span>
                        <Selo tom={h.recomendacao === 'HIBRIDO' ? 'navy' : 'neutro'}>{h.recomendacao === 'HIBRIDO' ? 'híbrido' : 'puro'}</Selo>
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-tinta-2">
        O DAS não muda: a LC 214 mantém alíquotas e deduções e só troca a partilha — o ICMS/ISS de dentro do DAS vira IBS ano a ano. Isso aumenta o crédito que o cliente empresa recebe
        de você e pode mudar a conta do híbrido ao longo da transição. No híbrido as guias são maiores porque o IBS/CBS é cobrado do cliente na nota; o que decide é a última coluna.{' '}
        <button className="text-navy underline" onClick={() => ir('simples')}>
          Ver puro × híbrido por ano
        </button>
      </p>
    </div>
  )
}
